import axios from "axios";
import { UAParser } from "ua-parser-js";

// Track if API call is in progress to prevent multiple concurrent calls
let isFetching = false;
let fetchPromise = null;

async function DeviceInfoAndLocation(setLocation, setDeviceInfo) {
  // Check sessionStorage first to prevent calls in the same session
  const sessionCalled = sessionStorage.getItem("deviceInfoApiCalled");
  if (sessionCalled === "true") {
    // Even if localStorage is cleared, don't call again in same session
    const cachedLocation = localStorage.getItem("userLocationData");
    const cachedDeviceInfo = localStorage.getItem("userDeviceInfo");
    
    if (cachedLocation && setLocation) {
      try {
        const locationData = JSON.parse(cachedLocation);
        if (!locationData?.error) {
          setLocation(locationData);
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    if (cachedDeviceInfo && setDeviceInfo) {
      try {
        const deviceData = JSON.parse(cachedDeviceInfo);
        if (deviceData?.browser || deviceData?.os) {
          setDeviceInfo(deviceData);
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    return;
  }

  // Always check cache first - this prevents any API calls if data exists
  const cachedLocation = localStorage.getItem("userLocationData");
  const cachedDeviceInfo = localStorage.getItem("userDeviceInfo");
  
  // If we have both cached, use them and return immediately (no API calls)
  if (cachedLocation && cachedDeviceInfo) {
    try {
      const locationData = JSON.parse(cachedLocation);
      if (!locationData?.error && setLocation) {
        setLocation(locationData);
      }
    } catch (e) {
      console.log("Error parsing cached location:", e);
    }
    
    try {
      const deviceData = JSON.parse(cachedDeviceInfo);
      if ((deviceData?.browser || deviceData?.os) && setDeviceInfo) {
        setDeviceInfo(deviceData);
      }
    } catch (e) {
      console.log("Error parsing cached device info:", e);
    }
    // Return early - no API calls needed
    return;
  }

  // If API call is already in progress, wait for it and then set the data
  if (isFetching && fetchPromise) {
    try {
      await fetchPromise;
      // After promise resolves, data should be in localStorage, so set it
      const newCachedLocation = localStorage.getItem("userLocationData");
      const newCachedDeviceInfo = localStorage.getItem("userDeviceInfo");
      
      if (newCachedLocation && setLocation) {
        try {
          const locationData = JSON.parse(newCachedLocation);
          if (!locationData?.error) {
            setLocation(locationData);
          }
        } catch (e) {
          console.log("Error parsing cached location:", e);
        }
      }
      
      if (newCachedDeviceInfo && setDeviceInfo) {
        try {
          const deviceData = JSON.parse(newCachedDeviceInfo);
          if (deviceData?.browser || deviceData?.os) {
            setDeviceInfo(deviceData);
          }
        } catch (e) {
          console.log("Error parsing cached device info:", e);
        }
      }
    } catch (error) {
      console.log("Error waiting for fetch promise:", error);
    }
    return;
  }

  // Only make API call if we don't have cached data and no call is in progress
  try {
    isFetching = true;
    
    fetchPromise = (async () => {
      try {
        // Get the user's public IP address
        const ipResponse = await axios.get("https://api.ipify.org/?format=json");
        const ipAddress = ipResponse.data.ip;

        // Fetch location data and device info concurrently
        const [locationResponse, deviceInfo] = await Promise.all([
          fetch(`https://ipapi.co/${ipAddress}/json/`).then((response) => response.json()),
          getDeviceInfo(),
        ]);

        // Mark as called in sessionStorage to prevent future calls in this session
        sessionStorage.setItem("deviceInfoApiCalled", "true");
        
        // Check if location data is valid and store it
        if (!locationResponse?.error) {
          localStorage.setItem("userLocationData", JSON.stringify(locationResponse));
          // Set location for all callers
          if (setLocation) {
            setLocation(locationResponse);
          }
        }

        // Check if device info contains browser or OS details and store it
        if (deviceInfo?.browser || deviceInfo?.os) {
          localStorage.setItem("userDeviceInfo", JSON.stringify(deviceInfo));
          // Set device info for all callers
          if (setDeviceInfo) {
            setDeviceInfo(deviceInfo);
          }
        }
      } catch (error) {
        console.log("Error fetching device info and location:", error);
        throw error;
      } finally {
        isFetching = false;
        fetchPromise = null;
      }
    })();

    await fetchPromise;
  } catch (error) {
    console.log("Error in DeviceInfoAndLocation:", error);
    isFetching = false;
    fetchPromise = null;
  }
}

async function getDeviceInfo() {
  const parser = new UAParser();
  const userAgent = parser.getResult();
  const deviceInfo = {
    device: userAgent?.device?.model || "",
    os: userAgent?.os?.name || "",
    browser: userAgent?.browser?.name || "",
  };
  return deviceInfo;
}

export default DeviceInfoAndLocation;
